import React from 'react';
import { FieldTimeOutlined } from '@ant-design/icons';
import DelayWidgetProcessor, {
	DelayWidgetState,
	getDefaultState,
} from '@kemu-io/kemu-core/widgets/delay/index.js';
import classNames from 'classnames';
import { WidgetPortContext } from '@kemu-io/kemu-core/types';
import useReactiveWidgetState from '../../../common/hooks/useReactiveWidgetState';
import { GetPortsInformationFunction, GateUI, GateUIProps } from '../index';
import GateIcon from '../../gateIcon/gateIcon';

import { PortLocation } from '../../../types/canvas_t';
import NumericWithoutArrows from '../../WidgetsComponents/NumericWithoutArrows/NumericWithoutArrows';
import styles from './delay.module.css';

type Props = GateUIProps;

type TimeMode = 'ms' | 'sec' | 'min';

const mergeStates = (state: DelayWidgetState) => {
	const fixedState: DelayWidgetState = {
		...getDefaultState(),
		...state
	};

	return fixedState;
};

const MIN_VALUE_MS = 1;
const MAX_VALUE_MS = 86400000; // 24 hours

const fixDecimals = (value: number) => Number(value.toFixed(2));
const getMinValue = (mode: TimeMode) => {
	switch (mode) {
		case 'ms': return MIN_VALUE_MS;
		case 'sec': return fixDecimals(MIN_VALUE_MS / 1000);
		case 'min': return fixDecimals(MIN_VALUE_MS / (1000 * 60));
		default: return MIN_VALUE_MS;
	}
};

const getMaxValue = (mode: TimeMode) => {
	switch (mode) {
		case 'ms': return MAX_VALUE_MS;
		case 'sec': return fixDecimals(MAX_VALUE_MS / 1000);
		case 'min': return fixDecimals(MAX_VALUE_MS / (1000 * 60));
		default: return MAX_VALUE_MS;
	}
};

const getFixedInterval = (interval: number, mode: TimeMode) => {
	switch (mode) {
		case 'ms': return interval;
		case 'sec': return fixDecimals(interval / 1000);
		case 'min': return fixDecimals(interval / (1000 * 60));
		default: return interval;
	}
};

const DelayWidget = (props: Props): React.JSX.Element => {
	const [state, setState] = useReactiveWidgetState<DelayWidgetState>(props.recipeId, props.thingRecipeId, props.info.id);
	const fixedState = mergeStates(state);
	const minValue = getMinValue(fixedState.intervalMode);
	const maxValue = getMaxValue(fixedState.intervalMode);

	const currentValue = getFixedInterval(fixedState.interval, fixedState.intervalMode);
	// Use the actual pending delays count from the state
	const activeDelaysCount = fixedState.pendingDelaysCount;

	const inputChangedCb = (newValue: number) => {
		let fixed: number;
		switch (fixedState.intervalMode) {
			case 'ms': fixed = newValue; break;
			case 'sec': fixed = fixDecimals(newValue * 1000); break;
			case 'min': fixed = fixDecimals(newValue * 1000 * 60); break;
			default: fixed = newValue;
		}
		setState({
			...state,
			interval: fixed,
		}, true);
	};

	const onToggleMode = () => {
		if (activeDelaysCount === 0) {
			const modes: TimeMode[] = ['ms', 'sec', 'min'];
			const currentIndex = modes.indexOf(fixedState.intervalMode);
			const nextMode = modes[(currentIndex + 1) % modes.length];

			setState({
				...fixedState,
				intervalMode: nextMode,
			});
		}
	};

	const isProcessing = activeDelaysCount > 0;

	return (
		<div className={classNames(styles.GateBody, { isProcessing })}>
			<div className={styles.InputContainer}>
				<div className={styles.InputRow}>
					<NumericWithoutArrows
						min={minValue}
						max={maxValue}
						value={currentValue}
						onChange={inputChangedCb}
						// Prevent changes while processing
						disabled={isProcessing}
					/>
					<span
						onClick={onToggleMode}
						className={classNames(styles.Suffix, { isProcessing })}
					>
						{fixedState.intervalMode}
					</span>
				</div>
			</div>
		</div>
	);
};

/** Icon to be added to the bar */
const WidgetBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<FieldTimeOutlined />}/>
	);
};


const getPortsInformation: GetPortsInformationFunction = (state, widgetInfo) => {
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};
	const outputNames = DelayWidgetProcessor.getOutputNames(state, portContext);
	const inputNames = DelayWidgetProcessor.getInputNames(state, portContext);

	const inputPositions: Record<string, PortLocation> = {
		'in': [0, 0.20, -1, 0],
		'abort': [0, 0.50, -1, 0],
		'continue': [0, 0.80, -1, 0],
	};

	return {
		inputs: inputNames.map(input => ({
			name: input.name,
			type: input.type,
			position: inputPositions[input.name]
		})),
		outputs: outputNames.map(output => ({
			name: output.name,
			type: output.type,
			position: 'Right'
		}))
	};
};

export default {
	getPortsInformation,
	BarIcon: WidgetBarIcon,
	Element: DelayWidget,
	hasTitle: true,
	getWidgetTitle: (intl) => intl.formatMessage({ defaultMessage: 'Delay', id: 'LogicMaker.Gates.Delay.Title' }),
} as GateUI;
